const INHERITABLE_FONT_PROPS = [
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
] as const;

const STROKE_PROPS = [
  'stroke',
  'stroke-width',
  'stroke-opacity',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-dasharray',
] as const;

const SHAPE_TAGS_FOR_STROKE = new Set([
  'rect',
  'circle',
  'ellipse',
  'path',
  'polygon',
  'polyline',
  'line',
  'g',
  'svg',
]);

type FontProp = typeof INHERITABLE_FONT_PROPS[number];

function parseCssDeclarations(declarationText: string): Map<string, string> {
  const props = new Map<string, string>();
  const declarations = declarationText.split(';');

  for (const decl of declarations) {
    const trimmed = decl.trim();
    if (!trimmed) continue;

    const colonIndex = trimmed.indexOf(':');
    if (colonIndex === -1) continue;

    const property = trimmed.substring(0, colonIndex).trim().toLowerCase();
    const value = trimmed.substring(colonIndex + 1).trim();

    if (property && value) {
      props.set(property, value);
    }
  }

  return props;
}

function extractClassRules(styleText: string): Map<string, Map<string, string>> {
  const classMap = new Map<string, Map<string, string>>();
  const cleaned = styleText.replace(/\/\*[\s\S]*?\*\//g, '');
  const ruleRegex = /\.([a-zA-Z_][\w-]*)\s*\{([^}]*)\}/g;

  let match: RegExpExecArray | null;
  while ((match = ruleRegex.exec(cleaned)) !== null) {
    const className = match[1];
    const declarations = parseCssDeclarations(match[2]);

    if (declarations.size > 0) {
      const existing = classMap.get(className);
      if (existing) {
        for (const [prop, val] of declarations) {
          existing.set(prop, val);
        }
      } else {
        classMap.set(className, new Map(declarations));
      }
    }
  }

  return classMap;
}

interface TagRule {
  tagName: string;
  properties: Map<string, string>;
}

function extractTagFontRules(styleText: string): TagRule[] {
  const rules: TagRule[] = [];
  const cleaned = styleText.replace(/\/\*[\s\S]*?\*\//g, '');
  const blockRegex = /([^{}]+)\{([^}]*)\}/g;

  let match: RegExpExecArray | null;
  while ((match = blockRegex.exec(cleaned)) !== null) {
    const selectorList = match[1].trim();
    const body = match[2];
    if (!selectorList || !body) continue;

    const declarations = parseCssDeclarations(body);
    if (declarations.size === 0) continue;

    const fontOnly = new Map<string, string>();
    for (const prop of INHERITABLE_FONT_PROPS) {
      const val = declarations.get(prop);
      if (val) fontOnly.set(prop, val);
    }
    if (fontOnly.size === 0) continue;

    const selectors = selectorList.split(',').map((s) => s.trim());
    for (const sel of selectors) {
      const parts = sel.split(/\s+/);
      const rightmost = parts[parts.length - 1];
      if (!rightmost) continue;

      const tagMatch = rightmost.match(/^([a-zA-Z][a-zA-Z0-9]*)$/);
      if (!tagMatch) continue;

      const tagName = tagMatch[1].toLowerCase();
      if (tagName !== 'text' && tagName !== 'tspan' && tagName !== 'g' && tagName !== 'svg') {
        continue;
      }

      rules.push({ tagName, properties: new Map(fontOnly) });
    }
  }

  return rules;
}

function extractTagStrokeRules(styleText: string): TagRule[] {
  const rules: TagRule[] = [];
  const cleaned = styleText.replace(/\/\*[\s\S]*?\*\//g, '');
  const blockRegex = /([^{}]+)\{([^}]*)\}/g;

  let match: RegExpExecArray | null;
  while ((match = blockRegex.exec(cleaned)) !== null) {
    const selectorList = match[1].trim();
    const body = match[2];
    if (!selectorList || !body) continue;

    const declarations = parseCssDeclarations(body);
    if (declarations.size === 0) continue;

    const strokeOnly = new Map<string, string>();
    for (const prop of STROKE_PROPS) {
      const val = declarations.get(prop);
      if (val) strokeOnly.set(prop, val);
    }
    if (strokeOnly.size === 0) continue;

    const selectors = selectorList.split(',').map((s) => s.trim());
    for (const sel of selectors) {
      const parts = sel.split(/\s+/);
      const rightmost = parts[parts.length - 1];
      if (!rightmost) continue;

      const tagMatch = rightmost.match(/^([a-zA-Z][a-zA-Z0-9]*)$/);
      if (!tagMatch) continue;

      const tagName = tagMatch[1].toLowerCase();
      if (!SHAPE_TAGS_FOR_STROKE.has(tagName)) continue;

      rules.push({ tagName, properties: new Map(strokeOnly) });
    }
  }

  return rules;
}

function serializeStyle(props: Map<string, string>): string {
  const parts: string[] = [];
  for (const [property, value] of props) {
    parts.push(`${property}: ${value}`);
  }
  return parts.join('; ');
}

function getElementOwnFontProps(el: Element): Map<FontProp, string> {
  const out = new Map<FontProp, string>();

  const styleAttr = el.getAttribute('style');
  if (styleAttr) {
    const parsed = parseCssDeclarations(styleAttr);
    for (const prop of INHERITABLE_FONT_PROPS) {
      const v = parsed.get(prop);
      if (v) out.set(prop, v);
    }
  }

  for (const prop of INHERITABLE_FONT_PROPS) {
    if (out.has(prop)) continue;
    const attrVal = el.getAttribute(prop);
    if (attrVal) out.set(prop, attrVal);
  }

  return out;
}

function mergeStyleWithFontProps(el: Element, fontProps: Map<FontProp, string>): void {
  if (fontProps.size === 0) return;

  const existingStyleAttr = el.getAttribute('style') || '';
  const existingProps = parseCssDeclarations(existingStyleAttr);

  const merged = new Map<string, string>();
  for (const [prop, val] of fontProps) {
    merged.set(prop, val);
  }
  for (const [prop, val] of existingProps) {
    merged.set(prop, val);
  }

  el.setAttribute('style', serializeStyle(merged));
}

export function preProcessSvgCss(svgString: string): string {
  if (!svgString || typeof svgString !== 'string') {
    return svgString;
  }

  const inputLength = svgString.length;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');

    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      console.warn('[svgCssPreParser] DOMParser encountered errors, returning original SVG');
      return svgString;
    }

    const styleElements = doc.querySelectorAll('style');

    const allClassRules = new Map<string, Map<string, string>>();
    const tagFontRules: TagRule[] = [];
    const tagStrokeRules: TagRule[] = [];

    styleElements.forEach((styleEl) => {
      const cssText = styleEl.textContent || '';
      const rules = extractClassRules(cssText);
      for (const [className, props] of rules) {
        const existing = allClassRules.get(className);
        if (existing) {
          for (const [prop, val] of props) {
            existing.set(prop, val);
          }
        } else {
          allClassRules.set(className, new Map(props));
        }
      }

      tagFontRules.push(...extractTagFontRules(cssText));
      tagStrokeRules.push(...extractTagStrokeRules(cssText));
    });

    for (const [className, properties] of allClassRules.entries()) {
      if (properties.has('font-size')) {
        console.log(`[SVG CSS] Class "${className}" has font-size rule: ${properties.get('font-size')} (all props: ${Array.from(properties.entries()).map(([k,v]) => `${k}: ${v}`).join('; ')})`);
      }
    }

    let inlinedCount = 0;

    const classScopedElements = doc.querySelectorAll('[class]');
    classScopedElements.forEach((el) => {
      const classAttr = el.getAttribute('class');
      if (!classAttr) return;

      const classNames = classAttr.trim().split(/\s+/);

      const classProperties = new Map<string, string>();
      for (const cn of classNames) {
        const rules = allClassRules.get(cn);
        if (rules) {
          for (const [prop, val] of rules) {
            classProperties.set(prop, val);
          }
        }
      }

      if (classProperties.size === 0) return;

      const existingStyleAttr = el.getAttribute('style') || '';
      const existingProps = parseCssDeclarations(existingStyleAttr);

      const merged = new Map<string, string>();
      for (const [prop, val] of classProperties) {
        merged.set(prop, val);
      }
      for (const [prop, val] of existingProps) {
        merged.set(prop, val);
      }

      el.setAttribute('style', serializeStyle(merged));
      inlinedCount++;

      const tagName = el.tagName?.toLowerCase() || '';
      if ((tagName === 'text' || tagName === 'tspan') && classProperties.has('font-size')) {
        console.log(`[SVG CSS] Inlined font-size onto <${tagName}> class="${el.getAttribute('class')}" → style="${el.getAttribute('style')?.substring(0, 200)}"`);
      }
    });

    let tagInlinedCount = 0;
    const applyTagRules = (rules: TagRule[]) => {
      for (const rule of rules) {
        const nodes = doc.getElementsByTagName(rule.tagName);
        for (let i = 0; i < nodes.length; i++) {
          const el = nodes[i];
          const existingStyleAttr = el.getAttribute('style') || '';
          const existingProps = parseCssDeclarations(existingStyleAttr);

          let applied = false;
          const merged = new Map<string, string>(existingProps);
          for (const [prop, val] of rule.properties) {
            if (!merged.has(prop)) {
              merged.set(prop, val);
              applied = true;
            }
          }
          if (applied) {
            el.setAttribute('style', serializeStyle(merged));
            tagInlinedCount++;
          }
        }
      }
    };
    applyTagRules(tagFontRules);
    applyTagRules(tagStrokeRules);

    const textLikeNodes: Element[] = [];
    const textNodes = doc.getElementsByTagName('text');
    for (let i = 0; i < textNodes.length; i++) textLikeNodes.push(textNodes[i]);
    const tspanNodes = doc.getElementsByTagName('tspan');
    for (let i = 0; i < tspanNodes.length; i++) textLikeNodes.push(tspanNodes[i]);

    let cascadedCount = 0;
    for (const node of textLikeNodes) {
      const own = getElementOwnFontProps(node);
      const missing = INHERITABLE_FONT_PROPS.filter((p) => !own.has(p));
      if (missing.length === 0) continue;

      const inherited = new Map<FontProp, string>();
      let cur: Element | null = node.parentElement;
      while (cur) {
        const parentProps = getElementOwnFontProps(cur);
        for (const prop of missing) {
          if (!inherited.has(prop) && parentProps.has(prop)) {
            inherited.set(prop, parentProps.get(prop)!);
          }
        }
        if (inherited.size === missing.length) break;
        cur = cur.parentElement;
      }

      if (inherited.size > 0) {
        mergeStyleWithFontProps(node, inherited);
        cascadedCount++;
      }
    }

    const didWork = inlinedCount > 0 || tagInlinedCount > 0 || cascadedCount > 0;

    if (didWork) {
      styleElements.forEach((styleEl) => {
        styleEl.parentNode?.removeChild(styleEl);
      });
    } else if (styleElements.length > 0) {
      console.log('[svgCssPreParser] No inlining performed; preserving <style> blocks for Fabric parser');
    }

    const serializer = new XMLSerializer();
    let result = serializer.serializeToString(doc.documentElement);

    result = result.replace(/\bns\d+:/g, '').replace(/\bxmlns:ns\d+="[^"]*"/g, '');

    if (result.length > inputLength * 10) {
      console.error(
        `[svgCssPreParser] Safety abort: output (${result.length}) exceeds 10x input (${inputLength}). Returning original.`
      );
      return svgString;
    }

    console.log(
      `[svgCssPreParser] Processed: ${inputLength.toLocaleString()} → ${result.length.toLocaleString()} chars (class:${inlinedCount} tag:${tagInlinedCount} cascade:${cascadedCount}, ${allClassRules.size} class rules, ${tagFontRules.length} font tag rules, ${tagStrokeRules.length} stroke tag rules)`
    );

    return result;
  } catch (err) {
    console.error('[svgCssPreParser] Unexpected error during processing, returning original SVG:', err);
    return svgString;
  }
}
